import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  StickyNote,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Calendar as CalendarIcon,
  Save,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getUserNotes,
  createUserNote,
  updateUserNote,
  deleteUserNote,
} from '../lib/api';
import { formatDate } from '../lib/utils';

export default function UserNotesDialog({ open, onOpenChange, targetUser }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingContent, setEditingContent] = useState('');

  const fetchNotes = useCallback(async () => {
    if (!targetUser?.id) return;
    setLoading(true);
    try {
      const res = await getUserNotes(targetUser.id);
      setNotes(res.data);
    } catch (err) {
      toast.error('Error al cargar las notas');
    } finally {
      setLoading(false);
    }
  }, [targetUser?.id]);

  useEffect(() => {
    if (open && targetUser?.id) {
      fetchNotes();
      setNewContent('');
      setEditingId(null);
      setEditingContent('');
    }
  }, [open, targetUser?.id, fetchNotes]);

  const handleCreate = async () => {
    if (!newContent.trim()) {
      toast.error('Escribe una nota antes de guardar');
      return;
    }
    setSubmitting(true);
    try {
      await createUserNote(targetUser.id, newContent.trim());
      toast.success('Nota agregada');
      setNewContent('');
      fetchNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar la nota');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditingContent(note.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingContent('');
  };

  const handleUpdate = async (noteId) => {
    if (!editingContent.trim()) {
      toast.error('La nota no puede estar vacía');
      return;
    }
    setSubmitting(true);
    try {
      await updateUserNote(targetUser.id, noteId, editingContent.trim());
      toast.success('Nota actualizada');
      cancelEdit();
      fetchNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('¿Eliminar esta nota?')) return;
    setSubmitting(true);
    try {
      await deleteUserNote(targetUser.id, noteId);
      toast.success('Nota eliminada');
      fetchNotes();
    } catch (err) {
      toast.error('Error al eliminar la nota');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-card border-border sm:max-w-2xl max-h-[85vh] flex flex-col"
        data-testid="user-notes-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="w-5 h-5 text-primary" />
            Notas — {targetUser?.name || targetUser?.username}
          </DialogTitle>
          <DialogDescription>
            Registra cortes de pago u observaciones. Cada nota guarda la fecha automáticamente.
          </DialogDescription>
        </DialogHeader>

        {/* New note form */}
        <Card className="bg-secondary/30 border-border/40">
          <CardContent className="p-4 space-y-3">
            <Textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder="Ej: Corte de pago realizado por $200 — Pendiente confirmar transferencia."
              className="bg-background/60 border-border/40 min-h-[80px]"
              data-testid="new-note-content"
            />
            <div className="flex justify-end">
              <Button
                onClick={handleCreate}
                disabled={submitting || !newContent.trim()}
                size="sm"
                className="gap-2"
                data-testid="save-new-note-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Agregar Nota
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Notes list */}
        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Cargando notas...
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <StickyNote className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Aún no hay notas para este usuario</p>
            </div>
          ) : (
            notes.map((note) => {
              const isEditing = editingId === note.id;
              const wasEdited = note.updated_at !== note.created_at;
              return (
                <Card
                  key={note.id}
                  className="bg-card border-border/40"
                  data-testid={`note-item-${note.id}`}
                >
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <Badge variant="outline" className="gap-1 font-mono">
                        <CalendarIcon className="w-3 h-3" />
                        {formatDate(note.created_at)}
                      </Badge>
                      {wasEdited && (
                        <span className="italic">editada {formatDate(note.updated_at)}</span>
                      )}
                    </div>
                    {isEditing ? (
                      <>
                        <Textarea
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value)}
                          className="bg-background/60 border-border/40 min-h-[70px]"
                          data-testid={`edit-note-input-${note.id}`}
                        />
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEdit}
                            disabled={submitting}
                            data-testid={`cancel-edit-${note.id}`}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Cancelar
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleUpdate(note.id)}
                            disabled={submitting || !editingContent.trim()}
                            data-testid={`save-edit-${note.id}`}
                          >
                            {submitting ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            ) : (
                              <Save className="w-4 h-4 mr-1" />
                            )}
                            Guardar
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-sm whitespace-pre-wrap break-words">{note.content}</p>
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEdit(note)}
                            data-testid={`edit-note-btn-${note.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5 mr-1" />
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleDelete(note.id)}
                            data-testid={`delete-note-btn-${note.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            Eliminar
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-notes-dialog">
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
